import { createBrowserRouter, Navigate, Outlet } from 'react-router';

import { DefinedUserPermissions } from '@workinance/shared/data/permissions';
import { type INavigationItem } from '@workinance/shared/ui/AppShellLayoutComponent';

import { DashboardLayoutComponent } from '@/components/dashboard/DashboardLayoutComponent';
import {
	primaryNavigation,
	rolesNavigationItem,
	usersNavigationItem,
	valueListsNavigationItem,
} from '@/components/dashboard/navigation';
import { LoadingScreenComponent } from '@/components/LoadingScreenComponent';
import {
	activateLoader,
	publicOnlyLoader,
	requireActivatedLoader,
	requireAuthLoader,
	requireBusinessLoader,
	requireFirstAccessibleRouteLoader,
	requireNavigationPermissionLoader,
	requirePosEnabledLoader,
	restoreSessionLoader,
} from '@/routes/route-guards';

// Looked up by path so each route's loader can reuse the exact same `requiredPermissions` its nav item
// declares, instead of a second list that could drift out of sync with the sidenav. A group such as
// CRM has no `to` of its own, so only its children (the ones that actually have a path) are collected.
function flattenByPath(
	items: INavigationItem[],
): Record<string, INavigationItem> {
	return Object.fromEntries(
		items.flatMap((item) =>
			item.children
				? Object.entries(flattenByPath(item.children))
				: [[item.to as string, item]],
		),
	);
}
const navigationItemsByPath = flattenByPath([
	...primaryNavigation,
	usersNavigationItem,
	rolesNavigationItem,
	valueListsNavigationItem,
]);
function requirePermissionLoaderFor(path: string) {
	return requireNavigationPermissionLoader(
		navigationItemsByPath[path]?.requiredPermissions,
	);
}

// Access is checked by each route's loader (see route-guards.ts), before the page renders.
export const applicationRoutes = createBrowserRouter([
	{
		// Reads the saved session before anything renders; the loading screen shows meanwhile.
		loader: restoreSessionLoader,
		HydrateFallback: LoadingScreenComponent,
		children: [
			{
				path: '/login',
				loader: publicOnlyLoader,
				children: [
					{
						index: true,
						lazy: () =>
							import('@/pages/auth/LoginPageComponent').then((m) => ({
								Component: m.LoginPageComponent,
							})),
					},
				],
			},
			{
				loader: requireAuthLoader,
				children: [
					{
						path: '/activate',
						loader: activateLoader,
						children: [
							{
								index: true,
								lazy: () =>
									import('@/pages/auth/ActivateAccountPageComponent').then(
										(m) => ({
											Component: m.ActivateAccountPageComponent,
										}),
									),
							},
						],
					},
					{
						loader: requireActivatedLoader,
						children: [
							{
								path: '/',
								loader: requireBusinessLoader,
								Component: DashboardLayoutComponent,
								children: [
									{
										index: true,
										element: <Navigate to="/dashboard" replace />,
									},
									{
										path: 'dashboard',
										lazy: () =>
											import('@/pages/DashboardHomePageComponent').then(
												(m) => ({
													Component: m.DashboardHomePageComponent,
												}),
											),
									},
									{
										path: 'crm',
										element: <Outlet />,
										children: [
											{
												index: true,
												loader: requireFirstAccessibleRouteLoader([
													{
														to: '/crm/customers',
														requiredPermissions: [
															DefinedUserPermissions.customer.view,
														],
													},
													{
														to: '/crm/surveys',
														requiredPermissions: [
															DefinedUserPermissions.survey.view,
														],
													},
												]),
											},
											{
												path: 'customers',
												loader: requireNavigationPermissionLoader([
													DefinedUserPermissions.customer.view,
												]),
												element: <Outlet />,
												children: [
													{
														index: true,
														lazy: () =>
															import('@/pages/crm/CustomersPageComponent').then(
																(m) => ({
																	Component: m.CustomersPageComponent,
																}),
															),
													},
													{
														path: ':customerId',
														lazy: () =>
															import('@/pages/crm/CustomerDetailPageComponent').then(
																(m) => ({
																	Component: m.CustomerDetailPageComponent,
																}),
															),
													},
												],
											},
											{
												path: 'surveys',
												loader: requireNavigationPermissionLoader([
													DefinedUserPermissions.survey.view,
												]),
												element: <Outlet />,
												children: [
													{
														index: true,
														lazy: () =>
															import('@/pages/crm/SurveysPageComponent').then(
																(m) => ({ Component: m.SurveysPageComponent }),
															),
													},
													{
														path: 'new',
														lazy: () =>
															import('@/pages/crm/SurveyBuilderPageComponent').then(
																(m) => ({
																	Component: m.SurveyBuilderPageComponent,
																}),
															),
													},
													{
														path: ':surveyId',
														lazy: () =>
															import('@/pages/crm/SurveyDetailPageComponent').then(
																(m) => ({
																	Component: m.SurveyDetailPageComponent,
																}),
															),
													},
												],
											},
										],
									},
									{
										path: 'inventory',
										element: <Outlet />,
										children: [
											{
												index: true,
												loader: requireFirstAccessibleRouteLoader([
													{
														to: '/inventory/products',
														requiredPermissions: [
															DefinedUserPermissions.product.view,
														],
													},
													{
														to: '/inventory/categories',
														requiredPermissions: [
															DefinedUserPermissions.productCategory.view,
														],
													},
												]),
											},
											{
												path: 'products',
												loader: requireNavigationPermissionLoader([
													DefinedUserPermissions.product.view,
												]),
												children: [
													{
														index: true,
														lazy: () =>
															import('@/pages/inventory/ProductsPageComponent').then(
																(m) => ({
																	Component: m.ProductsPageComponent,
																}),
															),
													},
													{
														path: ':productId',
														lazy: () =>
															import('@/pages/inventory/ProductDetailPageComponent').then(
																(m) => ({
																	Component: m.ProductDetailPageComponent,
																}),
															),
													},
												],
											},
											{
												path: 'categories',
												loader: requireNavigationPermissionLoader([
													DefinedUserPermissions.productCategory.view,
												]),
												lazy: () =>
													import('@/pages/inventory/ProductCategoriesPageComponent').then(
														(m) => ({
															Component: m.ProductCategoriesPageComponent,
														}),
													),
											},
										],
									},
									{
										path: 'sales',
										loader: requirePermissionLoaderFor('/sales'),
										element: <Outlet />,
										children: [
											{
												index: true,
												lazy: () =>
													import('@/pages/sales/SalesPageComponent').then(
														(m) => ({
															Component: m.SalesPageComponent,
														}),
													),
											},
											{
												path: 'new',
												lazy: () =>
													import('@/pages/sales/SaleOrderPageComponent').then(
														(m) => ({
															Component: m.SaleOrderPageComponent,
														}),
													),
											},
											{
												path: ':saleId',
												lazy: () =>
													import('@/pages/sales/SaleDetailPageComponent').then(
														(m) => ({
															Component: m.SaleDetailPageComponent,
														}),
													),
											},
											{
												path: ':saleId/edit',
												lazy: () =>
													import('@/pages/sales/SaleOrderPageComponent').then(
														(m) => ({
															Component: m.SaleOrderPageComponent,
														}),
													),
											},
										],
									},
									{
										path: 'pos',
										loader: async () =>
											(await requirePermissionLoaderFor('/pos')()) ??
											(await requirePosEnabledLoader()),
										lazy: () =>
											import('@/pages/pos/PosPageComponent').then((m) => ({
												Component: m.PosPageComponent,
											})),
									},
									{
										path: 'procurement',
										handle: { title: 'Procurement' },
										element: <Outlet />,
										children: [
											{
												index: true,
												loader: requireFirstAccessibleRouteLoader([
													{
														to: '/procurement/orders',
														requiredPermissions: [
															DefinedUserPermissions.purchaseOrder.view,
														],
													},
													{
														to: '/procurement/suppliers',
														requiredPermissions: [
															DefinedUserPermissions.supplier.view,
														],
													},
												]),
											},
											{
												path: 'orders',
												loader: requireNavigationPermissionLoader([
													DefinedUserPermissions.purchaseOrder.view,
												]),
												lazy: () =>
													import('@/pages/procurement/PurchaseOrdersPageComponent').then(
														(m) => ({
															Component: m.PurchaseOrdersPageComponent,
														}),
													),
											},
											{
												path: 'new',
												loader: requireNavigationPermissionLoader([
													DefinedUserPermissions.purchaseOrder.view,
												]),
												lazy: () =>
													import('@/pages/procurement/PurchaseOrderFormPageComponent').then(
														(m) => ({
															Component: m.PurchaseOrderFormPageComponent,
														}),
													),
											},
											{
												path: 'suppliers',
												loader: requireNavigationPermissionLoader([
													DefinedUserPermissions.supplier.view,
												]),
												element: <Outlet />,
												children: [
													{
														index: true,
														lazy: () =>
															import('@/pages/procurement/SuppliersPageComponent').then(
																(m) => ({
																	Component: m.SuppliersPageComponent,
																}),
															),
													},
													{
														path: ':supplierId',
														lazy: () =>
															import('@/pages/procurement/SupplierDetailPageComponent').then(
																(m) => ({
																	Component: m.SupplierDetailPageComponent,
																}),
															),
													},
												],
											},
											{
												path: ':purchaseOrderId',
												loader: requireNavigationPermissionLoader([
													DefinedUserPermissions.purchaseOrder.view,
												]),
												lazy: () =>
													import('@/pages/procurement/PurchaseOrderDetailPageComponent').then(
														(m) => ({
															Component: m.PurchaseOrderDetailPageComponent,
														}),
													),
											},
											{
												path: ':purchaseOrderId/edit',
												loader: requireNavigationPermissionLoader([
													DefinedUserPermissions.purchaseOrder.view,
												]),
												lazy: () =>
													import('@/pages/procurement/PurchaseOrderFormPageComponent').then(
														(m) => ({
															Component: m.PurchaseOrderFormPageComponent,
														}),
													),
											},
										],
									},
									{
										path: 'users',
										loader: requirePermissionLoaderFor('/users'),
										children: [
											{
												index: true,
												lazy: () =>
													import('@/pages/users/UsersPageComponent').then(
														(m) => ({
															Component: m.UsersPageComponent,
														}),
													),
											},
											{
												path: ':userId',
												lazy: () =>
													import('@/pages/users/UserDetailPageComponent').then(
														(m) => ({
															Component: m.UserDetailPageComponent,
														}),
													),
											},
										],
									},
									{
										path: 'roles',
										loader: requirePermissionLoaderFor('/roles'),
										children: [
											{
												index: true,
												lazy: () =>
													import('@/pages/roles/RolesPageComponent').then(
														(m) => ({
															Component: m.RolesPageComponent,
														}),
													),
											},
											{
												path: 'add',
												loader: requireNavigationPermissionLoader([
													DefinedUserPermissions.role.add,
												]),
												lazy: () =>
													import('@/pages/roles/RoleAddPageComponent').then(
														(m) => ({
															Component: m.RoleAddPageComponent,
														}),
													),
											},
											{
												path: ':roleId',
												lazy: () =>
													import('@/pages/roles/RoleDetailPageComponent').then(
														(m) => ({
															Component: m.RoleDetailPageComponent,
														}),
													),
											},
										],
									},
									{
										path: 'value-lists',
										loader: requirePermissionLoaderFor('/value-lists'),
										lazy: () =>
											import('@/pages/settings/ValueListsPageComponent').then(
												(m) => ({
													Component: m.ValueListsPageComponent,
												}),
											),
									},
									{
										path: 'profile',
										lazy: () =>
											import('@/pages/settings/ProfilePageComponent').then(
												(m) => ({
													Component: m.ProfilePageComponent,
												}),
											),
									},
								],
							},
						],
					},
				],
			},
			{ path: '*', element: <Navigate to="/" replace /> },
		],
	},
]);
